import { Box, Button, Link, Paper, Typography } from '@mui/material';
import { DataGridProProps, GRID_DETAIL_PANEL_TOGGLE_FIELD, GridRowParams } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { ReactElement } from 'react';
import { Breadcrumb } from '../../../breadcrumbs/models/Breadcrumb';
import { ExpandCell } from '../../../common/components/Cells/ExpandCell';
import { MenuCell } from '../../../common/components/Cells/MenuCell';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { SeverityCheckbox } from '../../../common/components/SeverityCheckbox';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { ProjectDetailsController } from '../../../common/dashboards/controllers/ProjectDetailsController';
import { Severity, getAll as getAllSeverities } from '../../../common/enums/Severity';
import { Preset } from '../../../common/models/valueObjects/Preset';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { bind } from '../../../common/utils/decorators';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { Project as PrefillProject } from '../../../modal-flows/models/Project';
import { SlotType } from '../../../modal-flows/models/SlotType';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { SlotService } from '../../../modal-flows/services/SlotService';
import { PATHS } from '../../../routing/routes';
import { OrganizationDataProvider } from '../../dashboard/providers/OrganizationDataProvider';
import { IssueBar } from '../components/IssueBar';
import { BlankOrganization } from '../components/blankSlates/BlankOrganization';
import { Project } from '../models/Project';
import { ProjectRepository } from '../services/ProjectRepository';

type Props = {};

type State = PaginatedTableState & {
  canCreateProject: boolean;
  hasUnlimitedReports: boolean;
  slotTypes: SlotType[];
  availableProfiles: Preset[];
  availableTags: Preset[];
  openProjectRowIds: string[];
  severities: Map<Severity, boolean>;
  loadingError: boolean;
};

export class OrganizationProjectsController extends PaginatedTableController<Project, Props, State> {
  private readonly repository = new ProjectRepository();
  private readonly organizationDataProvider = new OrganizationDataProvider();
  private readonly modalManager = new ModalManager();
  private readonly slotService = new SlotService();

  getBaseState(): State {
    return {
      ...super.getBaseState(),
      canCreateProject: false,
      hasUnlimitedReports: false,
      slotTypes: [],
      availableProfiles: [],
      availableTags: [],
      openProjectRowIds: [],
      severities: new Map(getAllSeverities().map((severity) => [severity, true])),
      loadingError: false,
    };
  }

  componentDidMount() {
    super.componentDidMount();

    this.load();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
    this.slotService.cancelAllRequests();
    this.organizationDataProvider.cancelAllRequests();
  }

  @bind
  getBreadcrumbs(): Breadcrumb[] {
    return this.breadcrumbFactory.getProjectsBreadcrumbs();
  }

  protected doInitialDataFetch(): void {
    // nothing, first we need to load some things
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<Project>> {
    try {
      const result = await this.repository.getList(cursorInfo, this.getFilters(), this.getSorters());
      // If a project is still in the result, keep it open
      const rowIds = result.data.map((p) => this.getRowId(p));

      // prefetch permissions
      const projectIds = result.data.map((p) => p.getId());
      await this.permissionsService.bulkPrefetchProjectPermissions(projectIds);

      this.setState((prev) => ({ openProjectRowIds: prev.openProjectRowIds.filter((i) => rowIds.includes(i)) }));
      return result;
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected getFieldConfigurations(): FieldConfiguration<Project>[] {
    const { severities, openProjectRowIds } = this.state;

    const columns: FieldConfiguration<Project>[] = [
      {
        fieldKey: GRID_DETAIL_PANEL_TOGGLE_FIELD,
        label: '',
        columnProps: { maxWidth: 52, minWidth: 52 },
        render: (item, { tabIndex }) => (
          <ExpandCell
            tabIndex={tabIndex}
            expanded={openProjectRowIds.includes(this.getRowId(item))}
            disabled={!item.getIssues().hasAny(severities)}
            onToggle={() => this.toggleProject(item)}
            openLabel={t('a11y.organization.projects.expandProjectRow', { projectId: item.getId(), projectName: item.getName() }) as string}
            closeLabel={t('a11y.organization.projects.collapseProjectRow', { projectId: item.getId(), projectName: item.getName() }) as string}
          />
        ),
      },
      {
        fieldKey: 'name',
        label: t('project'),
        sortable: true,
        columnProps: { minWidth: 250, maxWidth: 400 },
        render: (item, { tabIndex, hasFocus }) => (
          <Link
            tabIndex={tabIndex}
            color="text.primary"
            href={UrlHelper.generatePath(PATHS.PROJECT_DASHBOARD, { projectId: item.getId().toString() })}
            aria-label={t('a11y.organization.projects.linkToProjectDashboard', { projectId: item.getId(), projectName: item.getName() }) as string}
          >
            <TypographyOverflow hasFocus={hasFocus} alwaysOnTooltip={item.getDescription() ?? undefined}>
              {item.getName()}
            </TypographyOverflow>
          </Link>
        ),
      },
      {
        fieldKey: 'severities',
        label: '',
        columnProps: { minWidth: 500 },
        renderHeader: () => (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: 1 }}>
            <Typography component="span" fontWeight={500} fontSize="0.875rem">
              {t('issues')}
            </Typography>
            <Box sx={{ display: 'flex' }}>
              {[...severities.keys()].map((severity) => {
                const checked = Boolean(severities.get(severity));
                return (
                  <SeverityCheckbox
                    key={severity}
                    severity={severity}
                    checked={checked}
                    ariaLabelBase={checked ? 'a11y.organization.projects.toggleIssueSeverityOn' : 'a11y.organization.projects.toggleIssueSeverityOff'}
                    onChange={() => this.handleSeverityToggle(severity)}
                    disabled={this.isSeverityFilterDisabled(severity)}
                    size="small"
                  />
                );
              })}
            </Box>
          </Box>
        ),
        render: (item) => <IssueBar filter={severities} stats={item.getIssues()} />,
      },
      {
        fieldKey: 'profile',
        label: t('profile'),
        columnProps: { minWidth: 150, maxWidth: 300 },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getProfile()?.getLabel() || t('noProfile')}</TypographyOverflow>,
      },
      {
        fieldKey: 'lastAnalysis',
        label: t('lastAnalysis'),
        sortable: true,
        gqlKeys: { sorterField: 'lastAnalysis' },
        columnProps: { minWidth: 150, maxWidth: 300 },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getLastAnalysis()?.fromNow() || t('noReportsYet')}</TypographyOverflow>,
      },
      {
        fieldKey: 'actions',
        label: '',
        description: t('a11y.organization.projects.actions'),
        columnProps: { maxWidth: 60, minWidth: 60 },
        render: (item, { tabIndex }) => (
          <MenuCell
            tabIndex={tabIndex}
            actions={[
              {
                label: t('edit'),
                isActive: () => this.permissionsService.canUpdateProject(item.getId()),
                handler: () => this.handleUpdateProject(item),
              },
              {
                label: t('addTesters'),
                isActive: () => this.permissionsService.canUpdateProject(item.getId()),
                handler: () => this.handleAddTestersToProject(item),
              },
              {
                label: t('delete'),
                isActive: () => this.permissionsService.canDeleteProject(item.getId()),
                handler: () => this.handleDeleteProject(item),
              },
            ]}
          />
        ),
      },
    ];

    return columns;
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<Project>> {
    return {
      ...super.getAdditionalDataGridProps(),

      // Force the table to update when necessary
      nonce: this.getNonce(),

      // Use the custom row ID to update the table on project edit
      getRowId: (row) => this.getRowId(row),

      // Allow the expanded row to display all content
      getDetailPanelHeight: () => 'auto',

      // Control which rows are expanded based on internal state
      detailPanelExpandedRowIds: this.state.openProjectRowIds,

      // Render the expanded row
      getDetailPanelContent: this.renderProjectDetails,

      // Force the checkbox filter header to fill the column width
      sx: { '& .MuiDataGrid-columnHeader[data-field="severities"] .MuiDataGrid-columnHeaderTitleContainerContent': { width: 1 } },
    };
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'name',
        label: t('name'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
        ],
      },
      {
        filterKey: 'tags',
        label: t('tags'),
        type: FilterValueInputType.SELECT,
        options: this.state.availableTags.map((tag) => tag.toFilterFormat()),
        operations: [{ value: Operation.CONTAINS, label: t('filterLabels.has') }],
      },
      {
        filterKey: 'defaultProfile',
        label: t('profile'),
        type: FilterValueInputType.SELECT,
        options: this.state.availableProfiles.map((tag) => tag.toFilterFormat(true)),
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
      },
      {
        filterKey: 'lastAnalysis',
        label: t('lastAnalysis'),
        type: FilterValueInputType.DATE_TIME,
        operations: [
          { value: Operation.LT, label: t('filterLabels.isBefore') },
          { value: Operation.LE, label: t('filterLabels.isBeforeOrEqual') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.GE, label: t('filterLabels.isAfterOrEqual') },
          { value: Operation.GT, label: t('filterLabels.isAfter') },
        ],
      },
    ];
  }

  protected renderTable(tableComponent: ReactElement) {
    const { loadingData, initialDataLoad, hadAnyData, canCreateProject, loadingError } = this.state;
    const filters = this.getInitialFilters();

    // The "blank organization" screen is only displayed if there are no projects available on a first page load:
    // if there are stored filters in the URL, the empty table is displayed instead (all available projects might be excluded)
    const showBlankOrganization = !loadingData && !initialDataLoad && !hadAnyData && !loadingError && !filters;

    if (showBlankOrganization) {
      return (
        <Box sx={{ px: 'var(--secondary-outlet-px)', py: 'var(--secondary-outlet-py)' }}>
          <BlankOrganization onCreateNewProject={this.handleCreateNewProject} />
        </Box>
      );
    }

    return (
      <SecondaryNav
        title={t('projects')}
        subtitle={t('dashboardOverviewSubtitle')}
        rightSection={
          canCreateProject ? (
            <Button variant="contained" onClick={this.handleCreateNewProject}>
              {t('createNewProject')}
            </Button>
          ) : undefined
        }
        getBreadcrumbs={this.getBreadcrumbs}
      >
        {tableComponent}
      </SecondaryNav>
    );
  }

  @bind
  async handleCreateNewProject() {
    const { dismissed, projectId, reportId, integrationsClicked } = await this.modalManager.createProject(this.state.slotTypes, this.state.hasUnlimitedReports);

    // Project was not created, do nothing
    if (!projectId) return;

    // Navigate to project integrations
    if (integrationsClicked && !dismissed) {
      this.navigate(UrlHelper.generatePath(PATHS.PROJECT_INTEGRATIONS, { projectId: projectId.toString() }));
      return;
    }

    // A project was created and a report generated, navigate to it
    if (reportId && !dismissed) {
      this.navigate(UrlHelper.generatePath(PATHS.REPORT, { reportId: reportId.toString() }));
      return;
    }

    // At least one project was created, reset to page 1 and reload the projects
    this.reloadTableContent();
  }

  async handleUpdateProject(project: Project) {
    const prefillProject = new PrefillProject(project.getId(), project.getName(), project.getDescription(), null, project.getMaxItemCount(), []);
    const { projectId } = await this.modalManager.updateProject(prefillProject, this.state.hasUnlimitedReports);

    // Project was not updated, do nothing
    if (!projectId) return;

    // The project was updated, reset to page 1 and reload the projects
    this.reloadTableContent();

    // Clear the cached project breadcrumb
    this.breadcrumbCachingService.clearProject(projectId);
  }

  async handleAddTestersToProject(project: Project) {
    const { projectId } = await this.modalManager.addTestersToProject(project.getId());

    // Project was not updated, do nothing
    if (!projectId) return;

    // The project was updated, reset to page 1 and reload the projects
    this.reloadTableContent();
  }

  async handleDeleteProject(project: Project) {
    const { projectId } = await this.modalManager.deleteProject(project.getId(), project.getName());

    // Project was not deleted, do nothing
    if (!projectId) return;

    // The project was deleted, reset to page 1 and reload the projects
    this.reloadTableContent();

    // Clear the cached project breadcrumb
    this.breadcrumbCachingService.clearProject(projectId);
  }

  private async load() {
    try {
      // todo: propagate runtime fetching of these elements when available in FilterConfiguration
      // Right now, we are bound by the usage of native select inside of the FilterPanel.
      // Since we don't have an exposed api for all tags, we'll pull all by requesting 100 000 of them.
      const [availableTags, availableProfiles, hasUnlimitedReports, canCreateProject, slotTypes] = await Promise.all([
        this.organizationDataProvider.getTags('', [], 100_000),
        this.organizationDataProvider.getProfiles('', [], 100_000),
        this.organizationDataProvider.hasUnlimitedReports(),
        this.permissionsService.canCreateProject(),
        this.slotService.getSlotTypes(),
      ]);

      this.setState({ availableProfiles, availableTags, hasUnlimitedReports, canCreateProject, slotTypes }, () => {
        super.doInitialDataFetch();
      });
    } catch (e: unknown) {
      this.setupBlankState({ loadingError: true });
      this.handleError(e);
    }
  }

  @bind
  private toggleProject(project: Project) {
    const { openProjectRowIds } = this.state;
    const rowId = this.getRowId(project);
    if (openProjectRowIds.includes(rowId)) {
      this.setState({ openProjectRowIds: openProjectRowIds.filter((p) => p !== rowId) });
    } else {
      this.setState({ openProjectRowIds: [...openProjectRowIds, rowId] });
    }
  }

  @bind
  private renderProjectDetails({ row }: GridRowParams<Project>) {
    const { severities } = this.state;
    const activeSeverities = [...severities.entries()]
      .filter(([, isActive]) => isActive)
      .map(([severity]) => severity)
      .sort();

    // The project does not have any issue for the current severities, disable expansion
    if (!row.getIssues().hasAny(severities)) {
      return null;
    }

    return (
      <Box sx={{ p: 2, backgroundColor: 'backdrops.expandedRow' }}>
        <Paper>
          <ProjectDetailsController project={row} severities={activeSeverities} withFilteredView={false} />
        </Paper>
      </Box>
    );
  }

  /**
   * Builds a nonce that should change whenever the inner MemoizedMuiDataGrid needs to be forced to update.
   * Uses the following parameters:
   * - `severities`: the table header and expanded project details must reflect the checkbox selection
   * - `openProjectRowIds`: table rows must expand and collapse, and the project name must change upon edit
   */
  private getNonce(): string {
    const { severities, openProjectRowIds } = this.state;
    const severityNonce = Array.from(severities.entries())
      .map(([s, o]) => `${s}_${o}`)
      .join('_');
    const idNonce = openProjectRowIds.join('_');

    return [severityNonce, idNonce].join('_');
  }

  /**
   * Builds a row ID that should change whenever the inner MemoizedMuiDataGrid needs to be forced to update.
   * While `getNonce` is similar, this additional method handles the case of a single project edit when no other
   * change occurs in the table (severity checkbox change or row expansion)
   */
  private getRowId(project: Project): string {
    return `${project.getId()}_${project.getName()}_${project.getDescription()}`;
  }

  /**
   * We can toggle filters as long as we have at least one active.
   * If we have only one filter, then that one is disabled while the rest are active.
   *
   * With this approach, we avoid blank state situations where all filters are off.
   */
  private isSeverityFilterDisabled(type: Severity) {
    const { severities, loadingData, initialDataLoad, hadAnyData } = this.state;

    // Filter is disabled while the table is loading
    if (loadingData || initialDataLoad || !hadAnyData) {
      return true;
    }

    const numberOfActiveFilters = [...severities.values()].filter((v) => v);

    // If at least 2 are active, then everything is enabled.
    if (numberOfActiveFilters.length > 1) {
      return false;
    }

    // Since only one is active, disabled state depends on the current active state of a filter.
    return severities.get(type) ?? false;
  }

  private handleSeverityToggle(severity: Severity) {
    const { severities, openProjectRowIds } = this.state;

    const newSeverities = new Map([...severities, [severity, !severities.get(severity)]]);
    let newOpenProjects = [...openProjectRowIds];

    // If filters clear all available severities, close the open project
    for (const project of this.data) {
      const rowId = this.getRowId(project);
      if (newOpenProjects.includes(rowId) && !project.getIssues().hasAny(newSeverities)) {
        newOpenProjects = newOpenProjects.filter((id) => id !== rowId);
      }
    }

    this.setState({ severities: newSeverities, openProjectRowIds: newOpenProjects });
  }
}
